import { useState, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { ClaimMetadata } from '@/types/claims';
import { getErrorMessage, ApiError } from '@/lib/errors';

interface PollExtractionOptions {
    /** Maximum number of poll attempts (default: 30) */
    maxAttempts?: number;
    /** Interval between polls in ms (default: 2000) */
    pollIntervalMs?: number;
    /** Called with a status message on each phase change */
    onStatusChange?: (message: string) => void;
}

interface UseDocumentPollingResult {
    pollExtraction: (documentId: number, options?: PollExtractionOptions) => Promise<ClaimMetadata | null>;
    pollFullExtraction: (documentId: number) => Promise<boolean>;
    isPolling: boolean;
    error: string | null;
}

/**
 * Hook to handle polling for document extraction status
 */
export function useDocumentPolling(): UseDocumentPollingResult {
    const [isPolling, setIsPolling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const pollExtraction = useCallback(async (
        documentId: number,
        options?: PollExtractionOptions,
    ): Promise<ClaimMetadata | null> => {
        const {
            maxAttempts = 30,
            pollIntervalMs = 2000,
            onStatusChange,
        } = options ?? {};

        setIsPolling(true);
        setError(null);

        try {
            // Initial trigger
            onStatusChange?.('Extracting metadata from document...');
            try {
                await apiClient.documents.extractDocumentMetadata({ documentId });
            } catch (triggerError) {
                // 409 = extraction already in progress — safe to continue polling
                const isConflict =
                    triggerError instanceof ApiError && triggerError.status === 409;
                if (!isConflict) {
                    throw triggerError;
                }
                console.warn(`Metadata extraction already in progress for doc ${documentId}`);
            }

            onStatusChange?.('Waiting for metadata extraction...');
            const startTime = Date.now();
            let shownOcrMessage = false;

            for (let i = 0; i < maxAttempts; i++) {
                // Show OCR message after 15 seconds
                if (!shownOcrMessage && Date.now() - startTime > 15_000) {
                    shownOcrMessage = true;
                    onStatusChange?.('Applying OCR to scanned document — this may take a moment...');
                }

                // Wait before checking
                await new Promise(r => setTimeout(r, pollIntervalMs));

                const statusResponse = await apiClient.documents.getExtractionStatus({ documentId });
                const status = statusResponse.status;

                if (status === 'completed' || status === 'metadata_completed') {
                    const extractedData = await apiClient.documents.getDocumentExtractedData({ documentId });
                    return extractedData.metadata;
                }

                if (status === 'failed') {
                    throw new Error(statusResponse.error || 'Extraction failed on server');
                }
            }

            throw new Error('Metadata extraction timed out. You can continue with manual entry.');
        } catch (err) {
            const message = getErrorMessage(err);
            setError(message);
            throw err; // Re-throw for caller to handle if needed
        } finally {
            setIsPolling(false);
        }
    }, []);

    const pollFullExtraction = useCallback(async (documentId: number): Promise<boolean> => {
        setIsPolling(true);
        setError(null);

        try {
            // Trigger full extraction
            await apiClient.documents.extractDocument({ documentId });

            const POLL_INTERVAL = 3000;
            const MAX_ATTEMPTS = 60; // 3 minutes timeout for full extraction

            for (let i = 0; i < MAX_ATTEMPTS; i++) {
                await new Promise(r => setTimeout(r, POLL_INTERVAL));

                const statusResponse = await apiClient.documents.getExtractionStatus({ documentId });
                const status = statusResponse.status;

                if (status === 'completed') {
                    return true;
                }

                if (status === 'failed') {
                    throw new Error(statusResponse.error || 'Full extraction failed on server');
                }
            }

            throw new Error('Full extraction timed out');
        } catch (err) {
            const message = getErrorMessage(err);
            setError(message);
            throw err;
        } finally {
            setIsPolling(false);
        }
    }, []);

    return {
        pollExtraction,
        pollFullExtraction,
        isPolling,
        error
    };
}
